// The wire protocol. Both sides import these types; the server also validates every inbound
// message here, because nothing the client sends is trusted.

export const PROTOCOL_VERSION = 1;
export const TICK_HZ = 20;          // server simulation
export const SNAPSHOT_HZ = 10;      // state sent to clients
export const AOI_RADIUS = 900;      // px: what a player is told about

// ---------- shared value types ----------
export type ClassId = 'inkblade' | 'scribemage' | 'ranger' | 'warden';
export const CLASS_IDS: ClassId[] = ['inkblade', 'scribemage', 'ranger', 'warden'];

export type EntityKind = 'player' | 'enemy' | 'npc' | 'loot' | 'resource';

export interface EntityState {
  id: string;
  kind: EntityKind;
  /** definition id: class for players, enemy/npc/item id otherwise */
  def: string;
  name?: string;
  x: number;
  y: number;
  /** facing in radians */
  a: number;
  /** current movement speed, px/s (drives the client's animation) */
  v: number;
  hp?: number;
  hpMax?: number;
  /** server tick when this entity last swung; the client draws the impact */
  act?: number;
  dead?: boolean;
  ink?: string;
  /** players only: what they are wearing, see packEquip */
  eq?: string;
}

export interface ItemStack { slot: number; item: string; qty: number }
export const EQUIP_SLOTS = ['weapon', 'offhand', 'head', 'chest', 'boots', 'cape', 'trinket'] as const;
export type EquipSlot = typeof EQUIP_SLOTS[number];
export interface EquipState { weapon?: string; offhand?: string; head?: string; chest?: string; boots?: string; cape?: string; trinket?: string }
/** compact worn gear for snapshots: weapon|offhand|head|chest|boots|cape */
export const packEquip = (e: EquipState) => [e.weapon, e.offhand, e.head, e.chest, e.boots, e.cape].map(v => v ?? '').join('|');
export function unpackEquip(s: string | undefined): EquipState {
  if (!s) return {};
  const [weapon, offhand, head, chest, boots, cape] = s.split('|');
  return { weapon: weapon || undefined, offhand: offhand || undefined, head: head || undefined, chest: chest || undefined, boots: boots || undefined, cape: cape || undefined };
}
export interface QuestState { id: string; step: number; counters: Record<string, number>; done: boolean }

export interface CharacterState {
  id: string;
  name: string;
  cls: ClassId;
  ink: string;
  level: number;
  xp: number;
  xpNext: number;
  hp: number;
  hpMax: number;
  coins: number;
  inventory: ItemStack[];
  equip: EquipState;
  quests: QuestState[];
}

// ---------- client -> server ----------
export type ClientMsg =
  | { t: 'auth'; name: string; pass: string; cls?: ClassId; ink?: string }
  | { t: 'resume'; token: string }
  | { t: 'input'; seq: number; dx: number; dy: number; run?: boolean }
  | { t: 'attack'; target?: string; x?: number; y?: number }
  | { t: 'ability'; id: string; target?: string; x?: number; y?: number }
  | { t: 'interact'; target: string }
  | { t: 'pickup'; target: string }
  | { t: 'equip'; slot: number }
  | { t: 'unequip'; slot: EquipSlot }
  | { t: 'chat'; ch: 'local' | 'world'; text: string }
  | { t: 'ping'; s: number };

// ---------- server -> client ----------
export type ServerMsg =
  | { t: 'welcome'; you: string; token: string; char: CharacterState; zone: ZoneInfo; tick: number }
  | { t: 'denied'; why: string }
  | { t: 'snap'; tick: number; ents: EntityState[]; gone: string[] }
  | { t: 'char'; char: CharacterState }
  | { t: 'hit'; src: string; dst: string; dmg: number; crit?: boolean; kill?: boolean }
  | { t: 'chat'; from: string; ch: 'local' | 'world' | 'system'; text: string }
  | { t: 'note'; text: string; kind?: 'quest' | 'loot' | 'level' | 'info' }
  | { t: 'pong'; s: number; tick: number };

export interface ZoneInfo {
  id: string;
  title: string;
  w: number;
  h: number;
  /** static art + collision, sent once on join */
  props: ZoneProp[];
  spawn: { x: number; y: number };
}
export interface ZoneProp {
  kind: 'tree' | 'rock' | 'hut' | 'fence' | 'water' | 'sign' | 'bridge' | 'grass' | 'crate' | 'bush' | 'stump' | 'flowers' | 'barrel' | 'stall';
  x: number; y: number; w: number; h: number;
  /** blocks movement */
  solid?: boolean;
  seed: number;
  text?: string;
}

// ---------- validation (server side) ----------
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isStr = (v: unknown, max = 200): v is string => typeof v === 'string' && v.length <= max;

/** Returns a safe ClientMsg or null. Anything unexpected is dropped, never coerced. */
export function parseClientMsg(raw: unknown): ClientMsg | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.t) {
    case 'auth':
      if (!isStr(m.name, 24) || !isStr(m.pass, 128)) return null;
      return {
        t: 'auth', name: m.name, pass: m.pass,
        cls: CLASS_IDS.includes(m.cls as ClassId) ? (m.cls as ClassId) : undefined,
        ink: isStr(m.ink, 16) ? m.ink : undefined,
      };
    case 'resume':
      return isStr(m.token, 96) ? { t: 'resume', token: m.token } : null;
    case 'input':
      if (!isNum(m.seq) || !isNum(m.dx) || !isNum(m.dy)) return null;
      return { t: 'input', seq: m.seq, dx: clampUnit(m.dx), dy: clampUnit(m.dy), run: m.run === true };
    case 'attack':
      return { t: 'attack', target: isStr(m.target, 40) ? m.target : undefined, x: isNum(m.x) ? m.x : undefined, y: isNum(m.y) ? m.y : undefined };
    case 'ability':
      if (!isStr(m.id, 32)) return null;
      return { t: 'ability', id: m.id, target: isStr(m.target, 40) ? m.target : undefined, x: isNum(m.x) ? m.x : undefined, y: isNum(m.y) ? m.y : undefined };
    case 'interact':
      return isStr(m.target, 40) ? { t: 'interact', target: m.target } : null;
    case 'pickup':
      return isStr(m.target, 40) ? { t: 'pickup', target: m.target } : null;
    case 'equip':
      return isNum(m.slot) ? { t: 'equip', slot: Math.floor(m.slot) } : null;
    case 'unequip':
      return EQUIP_SLOTS.includes(m.slot as EquipSlot) ? { t: 'unequip', slot: m.slot as EquipSlot } : null;
    case 'chat':
      if (!isStr(m.text, 200)) return null;
      return { t: 'chat', ch: m.ch === 'world' ? 'world' : 'local', text: m.text };
    case 'ping':
      return isNum(m.s) ? { t: 'ping', s: m.s } : null;
    default:
      return null;
  }
}
const clampUnit = (v: number) => (v > 1 ? 1 : v < -1 ? -1 : v);
